import React, { useEffect, useState } from 'react';
import { QrCode, Download, ExternalLink, Loader2, Copy, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const QR_META = [
  {
    kind: 'portal',
    label: 'Espace Locataire',
    emoji: '🏠',
    description: 'Accès direct au portail locataire pour vos clients. À afficher dans votre agence.',
    color: 'from-blue-500 to-indigo-500',
  },
  {
    kind: 'payment',
    label: 'Paiement Loyer',
    emoji: '💳',
    description: 'Paiement Mobile Money (Orange Money & Moov) en 1 scan. À imprimer sur vos avis d\'échéance.',
    color: 'from-orange-500 to-amber-500',
  },
  {
    kind: 'site',
    label: 'Site de l\'Agence',
    emoji: '🌐',
    description: 'Vitrine de vos biens immobiliers. À mettre sur votre carte de visite ou vos flyers.',
    color: 'from-emerald-500 to-teal-500',
  },
];

const QrCodes = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewBlobs, setPreviewBlobs] = useState({}); // { [kind]: objectURL }
  const [downloading, setDownloading] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const loadAll = async () => {
    setLoading(true);
    try {
      const infoRes = await api.get('/qr-codes/info');
      setInfo(infoRes.data);
      // Fetch QR PNGs as blobs
      const newBlobs = {};
      await Promise.all(QR_META.map(async (m) => {
        try {
          const r = await api.get(`/qr-codes/preview?kind=${m.kind}`, { responseType: 'blob' });
          newBlobs[m.kind] = URL.createObjectURL(new Blob([r.data], { type: 'image/png' }));
        } catch (_) {
          newBlobs[m.kind] = null;
        }
      }));
      // Revoke old
      Object.values(previewBlobs).forEach((u) => u && URL.revokeObjectURL(u));
      setPreviewBlobs(newBlobs);
    } catch (e) {
      toast.error('Erreur lors du chargement des QR codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadAll();
    return () => {
      Object.values(previewBlobs).forEach((u) => u && URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, refreshKey]);

  const downloadPng = async (kind) => {
    setDownloading((d) => ({ ...d, [`${kind}-png`]: true }));
    try {
      const r = await api.get(`/qr-codes/preview?kind=${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'image/png' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr_${kind}_${info?.agency_name?.replace(/\s+/g, '_') || 'agence'}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('QR code téléchargé');
    } catch (e) {
      toast.error('Erreur de téléchargement');
    } finally {
      setDownloading((d) => ({ ...d, [`${kind}-png`]: false }));
    }
  };

  const downloadPoster = async (kind) => {
    setDownloading((d) => ({ ...d, [`${kind}-pdf`]: true }));
    try {
      const r = await api.get(`/qr-codes/poster?kind=${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `poster_${kind}_${info?.agency_name?.replace(/\s+/g, '_') || 'agence'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Poster PDF téléchargé');
    } catch (e) {
      toast.error('Erreur de téléchargement');
    } finally {
      setDownloading((d) => ({ ...d, [`${kind}-pdf`]: false }));
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url);
    toast.success('Lien copié');
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Accès réservé aux administrateurs</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="qr-codes-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1B3A4B] flex items-center gap-2">
            <QrCode className="w-6 h-6 text-[#D4A745]" />
            QR Codes Personnalisés
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            QR codes avec le logo de votre agence, prêts à imprimer ou à partager.
          </p>
        </div>
        <Button variant="outline" onClick={() => setRefreshKey(k => k + 1)} data-testid="refresh-qr-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Régénérer
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-6 h-6 animate-spin text-[#D4A745]" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {QR_META.map((m) => {
            const blobUrl = previewBlobs[m.kind];
            const type = info?.types?.find((t) => t.kind === m.kind);
            return (
              <Card key={m.kind} className="overflow-hidden border-2" data-testid={`qr-card-${m.kind}`}>
                <div className={`h-2 bg-gradient-to-r ${m.color}`} />
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <span className="text-2xl">{m.emoji}</span>
                    {m.label}
                  </CardTitle>
                  <CardDescription className="text-xs">{m.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* QR preview */}
                  <div className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl border flex items-center justify-center">
                    {blobUrl ? (
                      <img
                        src={blobUrl}
                        alt={`QR ${m.label}`}
                        className="w-full max-w-[220px] aspect-square object-contain"
                        data-testid={`qr-image-${m.kind}`}
                      />
                    ) : (
                      <div className="w-[220px] h-[220px] flex items-center justify-center text-gray-400">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    )}
                  </div>

                  {/* URL row */}
                  <div className="bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-2">
                    <code className="flex-1 text-[10px] text-gray-600 truncate">{type?.url || ''}</code>
                    <Button size="sm" variant="ghost" onClick={() => copyUrl(type?.url)} className="h-7 w-7 p-0">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <a href={type?.url} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadPng(m.kind)}
                      disabled={downloading[`${m.kind}-png`]}
                      data-testid={`download-png-${m.kind}`}
                    >
                      {downloading[`${m.kind}-png`] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      PNG
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => downloadPoster(m.kind)}
                      disabled={downloading[`${m.kind}-pdf`]}
                      className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                      data-testid={`download-poster-${m.kind}`}
                    >
                      {downloading[`${m.kind}-pdf`] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      Poster A4
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Info block */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4 text-sm text-amber-900 space-y-2">
          <p className="font-medium">💡 Comment utiliser ces QR codes ?</p>
          <ul className="list-disc list-inside space-y-1 text-amber-800">
            <li><strong>PNG</strong> : image haute qualité à insérer dans vos documents Word/Canva.</li>
            <li><strong>Poster A4</strong> : document prêt à imprimer, à afficher dans votre agence ou sur vos annonces.</li>
            <li>Les QR intègrent le <strong>logo et la couleur</strong> de votre agence (paramétrables dans Paramètres Agence).</li>
            <li>Les liens incluent votre <strong>ID agence</strong> pour un ciblage précis.</li>
          </ul>
          <div className="pt-2">
            <Badge className="bg-amber-200 text-amber-900">
              Agence : {info?.agency_name || '-'}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QrCodes;
